import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/base/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  if (!article) {
    return (
      <>
        <SEO title="文章未找到" description="抱歉，您查找的文章不存在。" />
        <Navbar />
        <main className="min-h-screen pt-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-4">
              文章未找到
            </h1>
            <Link
              to="/"
              className="text-primary-600 hover:text-primary-700 font-medium cursor-pointer whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = getRelatedArticles(article);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    image: article.imageUrl,
    url: window.location.href,
    publisher: {
      '@type': 'Organization',
      name: '韩国伦理文化研究',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': window.location.href,
    },
  };

  return (
    <>
      <SEO
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://korea-ethics.com/article/${slug}`}
        ogType="article"
        schema={articleSchema}
        articleDate={article.date}
        articleAuthor={article.author}
      />

      <Navbar />
      <ScrollToTop />

      <main className="min-h-screen pt-20">
        <article className="max-w-4xl mx-auto px-4 md:px-6 py-8">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: article.categoryName, href: `/category/${article.category}` },
              { label: article.title },
            ]}
          />

          <div className="mb-8">
            <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-4">
              {article.categoryName}
            </span>

            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-400 mb-6">
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i> {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i> {article.date}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i> {article.readTime} 分钟阅读
              </span>
            </div>

            <div className="flex flex-wrap gap-1.5 mb-6">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-2.5 py-1 bg-background-100 text-foreground-500 rounded-md text-xs"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>

          <div className="relative rounded-xl overflow-hidden h-64 md:h-80 mb-10">
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url('${article.imageUrl}')` }}
            ></div>
          </div>

          <div
            className="prose prose-lg max-w-none mb-16
              prose-headings:font-heading prose-headings:text-foreground-900 prose-headings:font-bold
              prose-h2:text-xl prose-h2:md:text-2xl prose-h2:mt-10 prose-h2:mb-4
              prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:text-sm prose-p:md:text-base
              prose-strong:text-foreground-900
              prose-ul:text-foreground-700 prose-ul:text-sm prose-ul:md:text-base
              prose-li:leading-relaxed"
            dangerouslySetInnerHTML={{ __html: article.content }}
          ></div>

          <div className="border-t border-background-200 pt-8">
            <div className="flex items-center gap-3 mb-6">
              <i className="ri-share-forward-line text-foreground-500"></i>
              <span className="text-sm font-medium text-foreground-600">分享文章</span>
              <a
                href="#"
                className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center text-foreground-500 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer"
                aria-label="分享到微博"
                rel="nofollow"
              >
                <i className="ri-weibo-line"></i>
              </a>
              <a
                href="#"
                className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center text-foreground-500 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer"
                aria-label="分享到微信"
                rel="nofollow"
              >
                <i className="ri-wechat-line"></i>
              </a>
              <a
                href="#"
                className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center text-foreground-500 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer"
                aria-label="复制链接"
                rel="nofollow"
              >
                <i className="ri-link"></i>
              </a>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="max-w-4xl mx-auto px-4 md:px-6 pb-16">
            <h2 className="font-heading text-xl font-bold text-foreground-900 mb-6">
              相关文章
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {relatedArticles.map((ra) => (
                <Link
                  key={ra.slug}
                  to={`/article/${ra.slug}`}
                  className="group bg-background-50 rounded-xl overflow-hidden border border-background-200 cursor-pointer transition-all duration-300 hover:-translate-y-0.5"
                >
                  <div className="h-36 relative overflow-hidden">
                    <div
                      className="absolute inset-0 bg-cover bg-center group-hover:scale-105 transition-transform duration-500"
                      style={{ backgroundImage: `url('${ra.imageUrl}')` }}
                    ></div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-heading text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {ra.title}
                    </h3>
                    <span className="text-xs text-foreground-400 mt-2 block">{ra.date}</span>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}